/*
 * Author: Dw
 * File Created: Thursday, 24-09-26 04:22 pm
 * Last Modified: Monday, 25-06-23 02:52 pm, by Dw
 */

/* eslint-disable no-unused-vars -- ignore */

import { NextResponse } from 'next/server';

import type { CustomAxiosRequestConfigReq } from '@web/http';
import { fmtMiddlewareReq, get } from '@web/http';
import {
  cookies,
  DEFAULT_ERROR_URL,
  getSafeRegex,
  isNativeApp,
  qsParse,
  query2Str,
  USE_CUSTOM_ERROR_PAGE,
} from '@web/utils';

import {
  IFRAME_SAME_SITE_NONE_CHANNEL,
  PREFIX_DECARD_WHITE_LIST_PAGE,
  PREFIX_OPEN_API_WHITE_LIST_PAGE,
  UA_EXP_WHITE_LIST,
} from './config';
import type { OPEN_API_ERROR_NAME } from './error';
import OpenApiError, { OpenApiErrorNameMap } from './error';
import type { OpenApiSecretInfo } from './type';

export const checkSecret = ({
  secret,
  req,
}: {
  secret: string;
  req: CustomAxiosRequestConfigReq;
}) => {
  return get<API.Redirect.GetSecretValidateV1Response>(
    '/gateway/redirect/public/v1/secret-validate',
    {
      secret,
    },
    { req },
  );
};

export const setOpenApiSession = (
  req: NextRequest,
  res: NextResponse,
  obj: Omit<API.Redirect.GetSecretValidateV1Response, 'userId' | 'referer'>,
) => {
  const queryStr = query2Str(obj);

  const logger = req.serverLogger;

  logger?.log(
    'OPEN_API_COOKIE, Start Set OPEN API COOKIE',
    queryStr,
    obj.externalUserId,
  );
  cookies.set(res.cookies, {
    key: 'x-open-id',
    value: queryStr,
    request: req,
    options: {
      secure: true,
      path: '/',
      sameSite: IFRAME_SAME_SITE_NONE_CHANNEL.includes(obj.channel ?? '')
        ? 'none'
        : 'lax',
      httpOnly: false,
    },
  });
};

export const getOpenApiSessionFromMiddleware = (
  req: NextRequest,
): Partial<API.Redirect.GetSecretValidateV1Response> => {
  const data = qsParse(req.cookies.get('x-open-id')?.value ?? '');

  return data as unknown as Partial<API.Redirect.GetSecretValidateV1Response>;
};

export const validateRunTime = (
  request: NextRequest,
  referer?: string,
  ua?: string,
): boolean => {
  const userAgent = request.headers.get('user-agent') || '';
  const localReferer = request.headers.get('referer') || '';

  const isUserAgentAllowed = UA_EXP_WHITE_LIST.some((regex) =>
    regex.test(userAgent),
  );

  const logger = request.serverLogger || {
    log: () => {
      // noop
    },
  };

  logger.log('Referer from res:', referer);
  logger.log('Referer from headers:', localReferer);

  logger.log('UA from res:', ua);
  logger.log('UA from headers:', userAgent);

  if (
    isUserAgentAllowed ||
    (referer && localReferer.includes(referer)) ||
    ua === userAgent
  ) {
    return true;
  }

  return false;
};

export const handleSecret = async (request: NextRequest) => {
  const { nextUrl, headers } = request;
  const secret = nextUrl.searchParams.get('secret');

  if (!secret) return null;

  const logger = request.serverLogger;

  try {
    const headersObject = Object.fromEntries(headers);

    logger?.log('OPEN API Secret', secret, headersObject);

    // todo need remove
    const { data } = await checkSecret({
      secret,
      req: fmtMiddlewareReq(request),
    });

    logger?.log('OPEN API checkSecret success', secret);

    if (data) {
      const {
        userId,
        externalUserId,
        channel,
        successRedirectUrl,
        errorRedirectUrl,
        mobileCode,
        mobile,
        email,
        referer,
        userAgent: ua,
        cardId,
        params,
      } = data as API.Redirect.GetSecretValidateV1Response;
      const isAllowed = validateRunTime(request, referer, ua);

      logger?.log('paramsparamsparamsparams', params);
      logger?.log('data', data);

      if (!isAllowed && channel !== 'APP') {
        throw new OpenApiError({
          message: `Secret: ${secret}, NOT IN THE UA WHITE_LIST, hostname: ${nextUrl.hostname}, referer: ${referer}, userAgent: ${ua}`,
          name: 'NOT_IN_WHITE_LIST',
          logger,
        });
      }

      const prefixPath = process.env.NEXT_PUBLIC_PREFIX_PATH || '';
      const pathname =
        prefixPath && nextUrl.pathname.startsWith(prefixPath)
          ? nextUrl.pathname
          : `${prefixPath}${nextUrl.pathname}`;

      nextUrl.searchParams.delete('secret');

      // 如果 Pathname 是 /card/detail, 需要将 cardId 添加到 url 中
      if (cardId) {
        // @ts-expect-error -- ignore
        nextUrl.searchParams.append('cardId', cardId);
      }

      if (params) {
        for (const k in params) {
          const v = params[k] as string;

          logger?.log('start append', k, v);

          v && nextUrl.searchParams.append(k, v);
        }
      }

      logger?.log('[with OpenApi] Final Pathname', pathname, secret);

      const redirectUrl = `${nextUrl.origin}${pathname}${nextUrl.search}`;

      console.log('nextURL', nextUrl);

      const redirectResponse = NextResponse.redirect(redirectUrl);

      logger?.log('[with OpenApi] redirectUrl', redirectUrl, secret);
      logger?.log('[with OpenApi] channel', channel, secret);

      cookies.set(redirectResponse.cookies, {
        key: 'p20t',
        value: `${channel}.${userId}.${secret}`,
        request,
        options: {
          httpOnly: true,
          secure: true,
          path: '/',
          // @ts-expect-error -- ignore
          sameSite: IFRAME_SAME_SITE_NONE_CHANNEL.includes(channel)
            ? 'none'
            : 'lax',
        },
      });

      cookies.set(redirectResponse.cookies, {
        key: 'x-login',
        value: 'Y',
        request,
        options: {
          httpOnly: false,
          secure: true,
          path: '/',
          // @ts-expect-error -- ignore
          sameSite: IFRAME_SAME_SITE_NONE_CHANNEL.includes(channel)
            ? 'none'
            : 'lax',
        },
      });

      setOpenApiSession(request, redirectResponse, {
        externalUserId,
        channel,
        successRedirectUrl,
        errorRedirectUrl,
        mobileCode,
        mobile,
        email,
        userAgent: ua,
      });

      return redirectResponse;
    }
  } catch (e) {
    let name: OPEN_API_ERROR_NAME | null | undefined;

    if (e instanceof OpenApiError) {
      if (e.name === 'NOT_IN_WHITE_LIST') {
        console.log('start redireact');
        name = e.name;
      }
    }

    // @ts-expect-error -- ignore
    if (e?.code === 'DAPI_PERMISSION_DENIED') {
      // @ts-expect-error -- ignore
      name = e.code;
    }

    logger?.error('OPEN API checkSecret error', e, name);

    return NextResponse.redirect(
      generateOpenApiErrorUrl(name ?? 'INTERNAL_ERROR', { request }),
    );
  }

  return null;
};

export const getOpenApiSession = (
  req?: CustomAxiosRequestConfig['req'],
): Partial<API.Redirect.GetSecretValidateV1Response> => {
  const cookieOpenId = cookies.get('x-open-id', req?.cookies as string) ?? '';

  return qsParse(
    decodeURIComponent(cookieOpenId),
  ) as unknown as Partial<API.Redirect.GetSecretValidateV1Response>;
};

function createRegexFromPattern(pattern: string): RegExp {
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&') // 转义正则特殊字符
    .replace(/\*\*/g, '.*') // `**` 匹配多级路径
    .replace(/\*/g, '[^/]+'); // `*` 匹配单级路径

  // 添加匹配前缀的逻辑，允许前缀部分是动态的
  return getSafeRegex(`.*${escaped}$`, '');
}

export const isPathAllowed = (
  pathname: string,
  channel?: OpenApiSecretInfo['channel'],
) => {
  const isPrefixWhiteListPage = PREFIX_OPEN_API_WHITE_LIST_PAGE.some(
    (pattern) => {
      try {
        const regex = createRegexFromPattern(pattern);

        // 检查路径是否符合正则，允许前面有任意内容
        return regex.test(pathname);
      } catch (error) {
        console.error(
          `Error in path-to-regexp for pattern2 "${pattern}":`,
          error,
        );

        return false;
      }
    },
  );

  const isDecardWhiteListPage =
    PREFIX_DECARD_WHITE_LIST_PAGE.some((pattern) => {
      try {
        const regex = createRegexFromPattern(pattern);

        // 检查路径是否符合正则，允许前面有任意内容
        return regex.test(pathname);
      } catch (error) {
        console.error(
          `Error in path-to-regexp for pattern2 "${pattern}":`,
          error,
        );

        return false;
      }
    }) && channel === 'APP';

  return isPrefixWhiteListPage || isDecardWhiteListPage;
};

export enum OPENAPI_POST_MESSAGE_TYPE {
  DECARD_OPENAPI_CLOSE = 'DECARD_OPENAPI_CLOSE',
  DECARD_OPENAPI_MFA_OPENED = 'DECARD_OPENAPI_MFA_OPENED',
  DECARD_OPENAPI_CARD_DETAIL_OPENED = 'DECARD_OPENAPI_CARD_DETAIL_OPENED',
}

declare global {
  interface Window {
    decardAndroid?: {
      postMessage?: (_message: string) => void;
    };
    webkit?: {
      messageHandlers?: {
        decardIOS?: {
          postMessage?: (_message: string) => void;
        };
      };
    };
  }
}

export const postNativeOpenAPIMessage = (
  name: OPENAPI_POST_MESSAGE_TYPE,
  payload?: Record<string, unknown> | null,
) => {
  if (typeof window === 'undefined') return;

  if (!isNativeApp()) {
    window.parent.postMessage({ name, payload: payload ?? null }, '*');

    return;
  }

  // for appSdk
  const jsonStr = JSON.stringify({
    name,
    payload: payload ?? null,
  });

  window.decardAndroid?.postMessage?.(jsonStr);

  // eslint-disable-next-line @typescript-eslint/dot-notation -- ignore
  window.webkit?.messageHandlers?.['decardIOS']?.postMessage?.(jsonStr);
};

// redirect to ERROR page.
export const generateOpenApiErrorUrl = (
  name: OPEN_API_ERROR_NAME | undefined | null,
  options?: {
    request?: NextRequest | CustomAxiosRequestConfig['req'];
  },
) => {
  const code = (name && OpenApiErrorNameMap[name]) ?? '10000';
  let openApiData: Partial<OpenApiSecretInfo> | null = {};

  if (typeof window !== 'undefined') {
    openApiData = qsParse(cookies.get('x-open-id') ?? {});
  } else if (process.env.NEXT_RUNTIME === 'edge') {
    if (!options?.request) {
      throw Error('options.request params is required in middleware runtime');
    }

    // @ts-expect-error -- ignore
    openApiData = getOpenApiSessionFromMiddleware(
      options.request as NextRequest,
    );
  } else {
    if (!options?.request) {
      throw Error('options.request params is required in node server');
    }

    // @ts-expect-error -- ignore
    openApiData = getOpenApiSession(
      options.request as CustomAxiosRequestConfig['req'],
    );
  }

  const { errorRedirectUrl, channel = '' } = openApiData || {};
  const targetUrl = USE_CUSTOM_ERROR_PAGE.includes(channel)
    ? errorRedirectUrl || DEFAULT_ERROR_URL
    : DEFAULT_ERROR_URL;

  const params = new URLSearchParams({ code });

  // 如果 errorRedirectUrl 已经包含查询参数，合并现有的查询参数并替换 code
  if (errorRedirectUrl?.includes('?')) {
    const existingParams = new URLSearchParams(errorRedirectUrl.split('?')[1]);

    existingParams.forEach((value, key) => {
      // 如果 key 是 'code'，则替换现有的 code 参数
      if (key === 'code') {
        params.set(key, code); // 使用新的 code 参数值替换
      } else if (!params.has(key)) {
        params.append(key, value); // 如果没有该参数，则追加
      }
    });
  }

  if (typeof window !== 'undefined') {
    // 客户端环境，直接返回完整 URL 字符串
    const fullUrl = `${targetUrl}?${params.toString()}`;

    return fullUrl;
  }

  if (process.env.NEXT_RUNTIME === 'edge') {
    // middleware
    const fullUrl = new URL(targetUrl);

    fullUrl.search = params.toString();

    return fullUrl;
  }

  // 服务端环境，返回完整 URL 字符串
  const fullUrl = `${targetUrl}?${params.toString()}`;

  return fullUrl;
};
